import { CheckCircleOutlined, RedoOutlined, UserOutlined, EyeOutlined, CalendarOutlined } from '@ant-design/icons';
import { Tag, Space, Popover, Button, Tabs, Modal, Input } from 'antd';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import config from '../../config';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import ReceptionJour from './ReceptionJour';
import Reception7Jour from './Reception7Jour';
import moment from 'moment/moment';
import ReceptionOne from './ReceptionOne';
import { toast } from 'react-toastify';
import {
  NdoePage, NdoePageHeader, NdoeToolbar, NdoeActionButton,
  NdoeResponsiveTable, NdoeStats, NdoeStatCard,
  NdoeDataCard, NdoeStatusBadge, NdoeRecherche,
} from '../../components/ndoe';

const Reception = () => {
    const DOMAIN = config.REACT_APP_SERVER_DOMAIN;
    const [getRapport, setGetRapport] = useState([]);
    const [loading, setLoading] = useState(true);
    // Recherche unifiée (16/07) : UNE seule zone libre. Elle joue sur DEUX
    // tableaux — filtrage instantané côté client (reçu par, type, quantités) ET
    // recherche serveur du PRODUIT (le serveur seul sait quelles lignes
    // contiennent tel article). D'où le bouton « Chercher » / la touche Entrée.
    const [q, setQ] = useState('');
    // Terme réellement envoyé au serveur lors du dernier « Chercher ». Sert à
    // ne PAS re-filtrer côté client une liste déjà filtrée par produit : sinon,
    // chercher « sandale » renverrait des réceptions… puis les masquerait toutes
    // (le mot n'apparaît dans aucune colonne visible).
    const [qServeur, setQServeur] = useState('');
    const [debut, setDebut] = useState('');
    const [fin, setFin] = useState('');
    const [seulementSansCout, setSeulementSansCout] = useState(false);
    const [opens, setOpens] = useState(false);
    const [date, setDate] = useState('');

const columns = [
    { title: '#', dataIndex: 'id', key: 'id', render: (text, record, index) => index + 1 },
    {
      title: 'Date & Heure',
      width: 176,
      dataIndex: 'date_reception',
      key: 'date_reception',
      sorter: (a, b) => a.date_reception - b.date_reception,
      sortDirections: ['descend', 'ascend'],
      render: (text) => (
        <Tag color={'blue'} icon={<CalendarOutlined />}>
          {moment(text).format('DD-MM-yyyy HH:mm')}
        </Tag>
      ),
    },
    {
      title: 'Type de mouvement',
      dataIndex: 'type_mouvement',
      key: 'type_mouvement',
      render: (type_mouvement) => (
        <Tag color={'green'} icon={<CheckCircleOutlined />}>
          {type_mouvement}
        </Tag>
      ),
    },
    {
      // Critères 16/07 : visibilité immédiate des coûts d'achat manquants
      // (alimente le rattrapage — Réceptions → Coûts d'achat).
      title: 'Coûts',
      dataIndex: 'lignes_sans_cout',
      key: 'lignes_sans_cout',
      render: (n) => (Number(n) > 0
        ? <Tag color="orange">{n} sans coût</Tag>
        : <Tag color="green">complets</Tag>),
    },
    {
      title: 'Nombre de paires',
      dataIndex: 'nombre_paires',
      key: 'nombre_paires',
      sorter: (a, b) => a.nombre_paires - b.nombre_paires,
      render: (nombre_paires) => (
        <Tag color={'blue'}>{nombre_paires}</Tag>
      ),
    },
    {
      title: 'Quantité totale',
      dataIndex: 'quantite_totale',
      key: 'quantite_totale',
      sorter: (a, b) => a.quantite_totale - b.quantite_totale,
      render: (quantite_totale) => (
        <Tag color={quantite_totale > 0 ? 'green' : 'red'}>{quantite_totale}</Tag>
      ),
    },
    {
      title: 'Reçu par',
      dataIndex: 'username',
      key: 'username',
      render: (username) => (
        <Tag color={'green'} icon={<UserOutlined />}>
          {username}
        </Tag>
      ),
    },
     {
      title: 'Action',
      key: 'action',
      render: (text, record) => (
        <Space size="middle">
           <Popover title="Voir les détails" trigger="hover">
            <Link onClick={()=>showModal(format(new Date(record.date_reception),'yyyy-MM-dd'))}>
              <Button icon={<EyeOutlined />} style={{ color: 'blue' }} />
            </Link>
          </Popover>
        </Space>
      ),
    }
];

const showModal = (e) => {
  setOpens(true);
  setDate(e)
};

// Fetch réutilisable (⑤) : sert au premier chargement ET au bouton Actualiser,
// à la place d'un window.location.reload() qui rechargeait toute l'app.
const fetchData = async () => {
  try {
    // La zone libre sert AUSSI de recherche produit côté serveur (seul le
    // serveur sait quelles réceptions contiennent tel article), et la période
    // remplace l'ancien panneau « Filtres par période ».
    const params = {};
    const terme = q.trim();
    if (terme) params.q = terme;
    if (debut) params.start_date = debut;
    if (fin) params.end_date = fin;
    const { data } = await axios.get(`${DOMAIN}/api/produit/reception`, { params });
    setQServeur(terme.toLowerCase());
    setGetRapport(data);
    return true;
  } catch (error) {
    console.log(error);
    toast.error('Erreur lors du chargement des réceptions');
    return false;
  } finally {
    setLoading(false);
  }
};

const Rafraichir = async () => {
  setLoading(true);
  const ok = await fetchData();
  if (ok) toast.success('Liste actualisée');
};

useEffect(() => {
  fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [DOMAIN]);

  // Filtrage INSTANTANÉ de la zone libre sur tout ce qui est affiché (reçu par,
  // type de mouvement, date). Le PRODUIT, lui, est filtré par le serveur au
  // moment du « Chercher » — d'où le fait qu'on n'exclue rien ici quand aucun
  // champ visible ne correspond mais que la liste vient d'une recherche produit.
  const filteredData = (getRapport || []).filter((item) => {
    if (seulementSansCout && !(Number(item.lignes_sans_cout) > 0)) return false;
    const t = q.trim().toLowerCase();
    if (!t) return true;
    const foin = [item.username, item.type_mouvement, item.date_reception]
      .filter(Boolean).join(' ').toLowerCase();
    // Si la liste PROVIENT d'une recherche produit serveur, le mot tapé peut ne
    // pas figurer dans les colonnes visibles : on garde alors la ligne (le
    // serveur a déjà tranché) plutôt que d'afficher « aucun résultat » à tort.
    return foin.includes(t) || qServeur === t;
  });

  // KPI dérivés de la liste (aucune donnée financière ici).
  const totalReceptions = getRapport?.length || 0;
  const totalPaires = getRapport?.reduce((sum, r) => sum + (Number(r.nombre_paires) || 0), 0) || 0;
  const totalQuantite = getRapport?.reduce((sum, r) => sum + (Number(r.quantite_totale) || 0), 0) || 0;

  // Carte "ligne" pour le mobile (remplace la ligne de tableau).
  const renderReceptionCard = (record) => {
    const voir = () => showModal(format(new Date(record.date_reception), 'yyyy-MM-dd'));
    return (
      <NdoeDataCard
        title={moment(record.date_reception).format('DD-MM-yyyy HH:mm')}
        subtitle={`${record.type_mouvement || 'Mouvement'} · Reçu par ${record.username || '—'}`}
        badge={
          <NdoeStatusBadge
            kind="custom"
            tone={record.quantite_totale > 0 ? 'ok' : 'danger'}
            label={`${record.quantite_totale ?? 0} u`}
          />
        }
        metrics={[
          { value: record.nombre_paires ?? 0, label: 'paires' },
          { value: record.quantite_totale ?? 0, label: 'quantité' },
        ]}
        onClick={voir}
        actions={[
          <NdoeActionButton key="v" size="sm" variant="ink" icon={<EyeOutlined />} onClick={voir}>
            Voir
          </NdoeActionButton>,
        ]}
      />
    );
  };

  // ----- Onglet "Réceptions" (liste + recherche + période) -----
  const tabReceptions = (
    <>
      {/* RECHERCHE UNIFIÉE (16/07) — même bloc que la liste des commandes, pour
          que la recherche s'apprenne UNE fois. Remplace : le champ « reçu par »,
          le champ « produit », la case « coûts manquants » ET le bouton
          « Filtres par période » (qui dépliait ReceptionSelect). */}
      <NdoeRecherche
        q={q}
        onQ={setQ}
        placeholder="Rechercher : produit, code article, reçu par, type… (Entrée pour chercher le produit)"
        periode={{ debut, fin }}
        onPeriode={(d, f) => { setDebut(d); setFin(f); }}
        onChercher={Rafraichir}
        chargement={loading}
        actifs={[q, seulementSansCout ? '1' : ''].filter(Boolean).length}
        onEffacer={() => { setQ(''); setSeulementSansCout(false); }}
        resume={(
          <>
            <b>{filteredData.length}</b>
            {filteredData.length !== getRapport.length && <> sur {getRapport.length}</>}
            &nbsp;réception{filteredData.length > 1 ? 's' : ''}
            {(debut || fin) && <span>· du {debut || '…'} au {fin || '…'}</span>}
          </>
        )}
      >
        {/* Bascule : les réceptions dont des lignes n'ont pas de coût d'achat
            — c'est ce qui alimente le rattrapage des marges. */}
        <button
          type="button"
          className={`ndoe-rech__bascule ${seulementSansCout ? 'is-on' : ''}`}
          aria-pressed={seulementSansCout}
          onClick={() => setSeulementSansCout((v) => !v)}
        >
          {seulementSansCout ? '✓ ' : ''}Coûts d’achat manquants
        </button>
      </NdoeRecherche>

      <NdoeToolbar>
        <NdoeActionButton variant="secondary" icon={<RedoOutlined />} onClick={Rafraichir}>
          Actualiser
        </NdoeActionButton>
      </NdoeToolbar>

      <NdoeResponsiveTable
        data={filteredData}
        columns={columns}
        rowKey="id"
        loading={loading}
        scroll={{ x: 'max-content' }}
        pagination={{ pageSize: 10 }}
        renderCard={renderReceptionCard}
        empty={{ icon: '📦', title: 'Aucune réception', text: 'Ajustez la recherche ou la période.' }}
      />
    </>
  );

  const tabItems = [
    { key: '0', label: 'Réceptions', children: tabReceptions },
    { key: '1', label: 'Réceptions du jour', children: <ReceptionJour /> },
    { key: '2', label: 'Réceptions des 7 derniers jours', children: <Reception7Jour /> },
  ];

  return (
    <NdoePage>
      <NdoePageHeader
        eyebrow="Stock"
        title="Réception"
        subtitle="Gérez vos réceptions"
      />

      <NdoeStats>
        <NdoeStatCard label="Réceptions" value={totalReceptions} />
        <NdoeStatCard label="Paires reçues" value={totalPaires} />
        <NdoeStatCard label="Quantité totale" value={totalQuantite} />
      </NdoeStats>

      <Tabs items={tabItems} />

      <Modal
        title={date && `Receptions de ${format(new Date(date), 'dd-MM-yyyy')}`}
        centered
        open={opens}
        onCancel={() => setOpens(false)}
        width={1200}
        footer={[]}
      >
        <ReceptionOne dateId={date} />
      </Modal>
    </NdoePage>
  )
}

export default Reception
